import * as React from 'react';
import {without} from 'lodash';

type KeyboardContextShape = {
  readonly addListener: (arg1: Listener) => void;
  readonly removeListener: (arg1: Listener) => void;
};
type Listener = (arg1: KeyboardEvent) => boolean | null | undefined;
type EventTarget = {
  addEventListener(
    event: 'keyup',
    listener: (e: KeyboardEvent) => void,
    capture?: false
  ): void;
  removeEventListener(
    event: 'keyup',
    listener: (e: KeyboardEvent) => void,
    capture?: false
  ): void;
};
type Props = {
  // Note that the names of these props precedes alignment with the type names
  // so eventSource and EventTarget are referring to different things.
  eventSource?: EventTarget;
  children: React.ReactNode;
};

// @ts-expect-error - TS2739 - Type '{}' is missing the following properties from type 'KeyboardContextShape': addListener, removeListener
const emptyContext: KeyboardContextShape = {};
export const Context = React.createContext<KeyboardContextShape>(emptyContext);

export default class KeyboardContext extends React.Component<Props> {
  declare eventSource: EventTarget;
  listeners: Array<Listener> = [];

  addListener = (fn: Listener) => {
    this.listeners = this.listeners.concat(fn);
  };

  removeListener = (fn: Listener) => {
    this.listeners = without(this.listeners, fn);
  };

  // keeping an instance variable to not rebuild this. This would typically be
  // done with setState, but listeners is a mutable reference without triggering
  // rerendering so we can apply this optimization
  _context: KeyboardContextShape = {
    addListener: this.addListener,
    removeListener: this.removeListener,
  } as any;

  handleEvent = (event: KeyboardEvent) => {
    const {listeners} = this;

    for (let i = listeners.length - 1; i >= 0; i--) {
      // @ts-expect-error - TS2722 - Cannot invoke an object which is possibly 'undefined'.
      if (listeners[i](event) === false) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }
  };

  componentDidMount() {
    // Make sure we use the same event source for the entire life
    // of the element in order to avoid memory leaks.
    if (this.props.eventSource) {
      if (this.eventSource) {
        this.eventSource.removeEventListener('keyup', this.handleEvent);
      }
      this.eventSource = this.props.eventSource;
      this.eventSource.addEventListener('keyup', this.handleEvent, false);
    }
  }

  componentWillUnmount() {
    this.eventSource.removeEventListener('keyup', this.handleEvent, false);
    // @ts-expect-error - TS2322 - Type 'null' is not assignable to type 'EventTarget'.
    this.eventSource = null;
  }

  handleRef = (node: HTMLDivElement | null) => {
    if (!this.eventSource && node) {
      this.eventSource = node?.ownerDocument as EventTarget;
      this.eventSource?.addEventListener('keyup', this.handleEvent, false);
    }
  };

  render() {
    return (
      <Context.Provider value={this._context}>
        <div ref={this.handleRef}>{this.props.children}</div>
      </Context.Provider>
    );
  }
}
